import { useEffect, useMemo, useState } from 'react';
import { Avatar, Button, List, Space, Typography } from 'antd';
import { CloseOutlined, UserOutlined } from '@ant-design/icons';
import {
  getAccountName,
  getAccountSubtitle,
  getActiveAccount,
  useAccountSelectionStore,
} from '@core/store/accountSelectionStore';
import { getAccountTheme } from '@core/utils/accountRuntime';
import AppModal from '@components/base/AppModal';

function initials(name) {
  return String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase();
}

function AccountRow({ account, isSelected, onSelect }) {
  const title = getAccountName(account);
  const subtitle = getAccountSubtitle(account);
  const logoUrl = getAccountTheme(account)?.logoUrl || '';

  return (
    <List.Item
      className={`account-row-item ${isSelected ? 'selected' : ''}`}
      onClick={onSelect}
      style={{
        cursor: 'pointer',
        padding: 16,
        borderRadius: 18,
        border: isSelected ? '1px solid var(--color-primary)' : '1px solid #e7e7ea',
        background: isSelected ? 'var(--mobile-checkout-active-bg)' : '#fff',
        marginBottom: 14,
      }}
    >
      <List.Item.Meta
        className="account-row-meta"
        avatar={
          <Avatar
            className="account-row-avatar"
            src={logoUrl || undefined}
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: logoUrl
                ? '#fff'
                : isSelected
                  ? 'var(--color-primary)'
                  : 'rgba(0,0,0,0.06)',
              color: isSelected ? '#fff' : 'var(--color-text)',
              fontWeight: 700,
              overflow: 'hidden',
            }}
          >
            {!logoUrl ? initials(title) : null}
          </Avatar>
        }
        title={
          <Space className="account-row-title-space" size={8} wrap>
            <Typography.Text className="account-row-title" strong style={{ fontSize: 16 }}>
              {title}
            </Typography.Text>
          </Space>
        }
        description={
          <Typography.Text className="account-row-subtitle" type="secondary">
            {subtitle || account.id}
          </Typography.Text>
        }
      />
      <div
        className="account-row-select-circle"
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: isSelected ? '2px solid var(--color-primary)' : '2px solid #b7b7bf',
          display: 'grid',
          placeItems: 'center',
          marginLeft: 16,
          flex: '0 0 auto',
        }}
      >
        {isSelected ? (
          <div
            className="account-row-select-dot"
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: 'var(--color-primary)',
            }}
          />
        ) : null}
      </div>
    </List.Item>
  );
}

export default function AccountSelectionModal() {
  const availableAccounts = useAccountSelectionStore((s) => s.availableAccounts);
  const activeAccountId = useAccountSelectionStore((s) => s.activeAccountId);
  const accountSelectorOpen = useAccountSelectionStore((s) => s.accountSelectorOpen);
  const closeAccountSelector = useAccountSelectionStore((s) => s.closeAccountSelector);
  const selectAccount = useAccountSelectionStore((s) => s.selectAccount);
  const hasLinkedAccounts = useAccountSelectionStore((s) => s.hasLinkedAccounts);

  const currentAccount = getActiveAccount();
  const [draftAccountId, setDraftAccountId] = useState(activeAccountId || '');
  const isOpen = accountSelectorOpen && hasLinkedAccounts;
  const selectedAccount = useMemo(
    () =>
      availableAccounts.find((account) => account.id === draftAccountId) ||
      currentAccount ||
      availableAccounts[0] ||
      null,
    [availableAccounts, currentAccount, draftAccountId],
  );

  useEffect(() => {
    if (!isOpen) return;
    setDraftAccountId(activeAccountId || currentAccount?.id || availableAccounts[0]?.id || '');
  }, [activeAccountId, availableAccounts, currentAccount?.id, isOpen]);

  const isRequired = !activeAccountId;

  return (
    <AppModal
      kind="account-select"
      className="account-selection-modal"
      open={isOpen}
      closeIcon={!isRequired ? <CloseOutlined style={{ fontSize: 18, color: '#6b7280' }} /> : null}
      onCancel={!isRequired ? closeAccountSelector : undefined}
      maskClosable={!isRequired}
      keyboard={!isRequired}
      closable={!isRequired}
      footer={null}
      destroyOnHidden={false}
      width={620}
      zIndex={10000}
      centered
      styles={{
        content: {
          borderRadius: 10,
        },
      }}
    >
      <Space
        className="account-selection-container"
        direction="vertical"
        size={2}
        style={{ width: '100%' }}
      >
        <Space
          className="account-selection-header"
          align="start"
          size={14}
          style={{ width: '100%' }}
        >
          <div
            className="account-selection-icon-wrapper"
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'var(--mobile-checkout-active-bg)',
              display: 'grid',
              placeItems: 'center',
              flex: '0 0 auto',
            }}
          >
            <UserOutlined
              className="account-selection-icon"
              style={{ fontSize: 24, color: 'var(--color-primary)' }}
            />
          </div>
          <div className="account-selection-title-col" style={{ flex: 1, paddingBottom: 8 }}>
            <Typography.Title
              className="account-selection-title"
              level={4}
              style={{ margin: 0, lineHeight: 1.2 }}
            >
              Select account
            </Typography.Title>
            <Typography.Text className="account-selection-subtitle" type="secondary">
              Choose the account you want to use for this session
            </Typography.Text>
          </div>
        </Space>

        <List
          className="account-selection-list"
          dataSource={availableAccounts}
          split={false}
          renderItem={(account) => (
            <AccountRow
              key={account.id}
              account={account}
              isSelected={draftAccountId === account.id}
              onSelect={() => setDraftAccountId(account.id)}
            />
          )}
        />

        <div
          className="account-selection-footer"
          style={{
            paddingTop: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            className="account-selection-btn"
            type="primary"
            size="middle"
            onClick={() => {
              if (selectedAccount) {
                selectAccount(selectedAccount);
              }
            }}
            style={{
              minWidth: 142,
              borderRadius: 12,
              fontWeight: 600,
            }}
          >
            Continue
          </Button>
        </div>
      </Space>
    </AppModal>
  );
}
